import { Autocomplete, Box, Button, CircularProgress, Paper, TextField, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import styles from "../../content/pages/homepage.module.css";
import { countries } from "src/mockup/countries";
import { useEffect, useState } from "react";
import { createStripeSubscription } from "src/api/createStripeSubscription";
import { useSafeAccessAuth0Token } from "src/hooks/useSafeAccessAuth0Token";
import PaymentDialog from "./PaymentDialog";
import { getProductConfig } from "src/config/products.config";
import { useContext } from "react";
import { WidgetContext } from "src/contexts/WidgetContext";
import { subscribeFrontRuntimeConfig } from "src/services/runtimeFrontConfig";
import { useAuth0 } from "@auth0/auth0-react";
import { planDurationFromProductId, trackBeginCheckout, trackPurchase, trackSubscriptionFailed } from "src/utils/analyticsCapFirebase";

interface CheckoutFormProps {
  selectedProductId: string;
  promotionCodeId?: string; // Optional promotion code ID from ManageMembership
  promoPercentOff?: number | null; // Optional percentage discount
  promoAmountOff?: number | null; // Optional fixed amount discount
  onSuccess: (credits: number) => void;
  onClose: () => void; // Close Stripe form only
  onDrawerClose: () => void; // Close the entire drawer (AddPaymentMethod)
}

const CheckoutForm = ({ selectedProductId, promotionCodeId, promoPercentOff, promoAmountOff, onSuccess, onClose, onDrawerClose }: CheckoutFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const { getSafeAccessAuth0Token } = useSafeAccessAuth0Token();
  const { seasonPassPrice } = useContext(WidgetContext);
  const { user } = useAuth0();
  const analyticsAuthId = user?.sub || "unknown";

  const [, setRuntimeRefresh] = useState(0);
  useEffect(() => subscribeFrontRuntimeConfig(() => setRuntimeRefresh((n) => n + 1)), []);

  // Get product configuration for display
  // For season_pass, use seasonPassPrice from context
  const productConfig = selectedProductId === "league_connect_season_pass" ? getProductConfig(selectedProductId, seasonPassPrice || "1") : getProductConfig(selectedProductId);

  // Calculate discounted price
  const calculateDiscountedPrice = () => {
    if (!productConfig) return null;

    let discountedPrice = productConfig.price;

    if (promoPercentOff) {
      // Percentage discount
      discountedPrice = productConfig.price * (1 - promoPercentOff / 100);
    } else if (promoAmountOff) {
      // Fixed amount discount
      discountedPrice = Math.max(0, productConfig.price - promoAmountOff);
    }

    return discountedPrice;
  };

  const discountedPrice = calculateDiscountedPrice();
  const hasDiscount = discountedPrice !== null && discountedPrice < (productConfig?.price || 0);

  //State for selected and pass countries
  const [selectedCountry, setSelectedCountry] = useState<{ label: string; code: string } | null>(null); //state for selected country
  const [firstName, setFirstName] = useState(""); //state for first name
  const [lastName, setLastName] = useState(""); //state for last name
  const [address, setAddress] = useState(""); //state for address
  const [error, setError] = useState(""); //state for error
  const [isValid, setIsValid] = useState(false); //state for form validation
  const [isProcessing, setIsProcessing] = useState(false); //state for processing
  const [cardComplete, setCardComplete] = useState(false); //state for card completion
  const [showSuccessCheck, setShowSuccessCheck] = useState(false); //state for showing success check
  const [showPaymentDialog, setShowPaymentDialog] = useState(false); //state for showing payment dialog
  const [paymentDialogSuccess, setPaymentDialogSuccess] = useState(false); //state for payment dialog success
  const [paymentDialogMessage, setPaymentDialogMessage] = useState<string | undefined>(undefined); //state for payment dialog message

  // Validate card element using change event
  useEffect(() => {
    if (!stripe || !elements) {
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      return;
    }

    const handleCardChange = (event: any) => {
      if (event.error) {
        setError(event.error.message);
        setCardComplete(false);
      } else {
        setError("");
        setCardComplete(event.complete);
      }
    };

    cardElement.on("change", handleCardChange);

    return () => {
      cardElement.off("change", handleCardChange);
    };
  }, [stripe, elements]);

  // Update isValid when all required fields are valid
  useEffect(() => {
    const newIsValid = cardComplete && selectedCountry !== null && firstName.trim() !== "" && lastName.trim() !== "" && address.trim() !== "";
    console.log("🔍 Form validation:", {
      cardComplete,
      selectedCountry: selectedCountry?.label,
      firstName: firstName.trim() !== "",
      lastName: lastName.trim() !== "",
      address: address.trim() !== "",
      isValid: newIsValid,
    });
    setIsValid(newIsValid);
  }, [cardComplete, selectedCountry, firstName, lastName, address]);

  const handleCountryChange = (event: React.SyntheticEvent, newValue: { label: string; code: string } | null) => {
    console.log("🌍 Country changed:", newValue?.label);
    setSelectedCountry(newValue);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    console.log("🔘 PAY button clicked");
    console.log("🔍 Form state:", { isValid, cardComplete, selectedCountry: selectedCountry?.label, selectedProductId });

    if (!stripe || !elements) {
      setError("Stripe is not loaded. Please wait...");
      return;
    }

    if (!cardComplete) {
      setError("Please complete the card information.");
      return;
    }

    if (!selectedCountry) {
      setError("Please select a country.");
      return;
    }

    if (!selectedProductId) {
      setError("No product selected.");
      return;
    }

    setIsProcessing(true);
    setError("");

    try {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        setError("Card element not found. Please refresh the page.");
        setIsProcessing(false);
        return;
      }

      // Validate required fields
      if (!firstName.trim() || !lastName.trim() || !address.trim() || !selectedCountry) {
        setError("Please fill in all required fields.");
        setIsProcessing(false);
        return;
      }

      // Create payment method with Stripe
      const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: {
          name: `${firstName.trim()} ${lastName.trim()}`,
          address: {
            line1: address.trim(),
            country: selectedCountry.code,
          },
        },
      });

      if (pmError || !paymentMethod) {
        console.error("❌ Payment method creation error:", pmError);
        setError(pmError?.message || "Failed to create payment method");
        setIsProcessing(false);
        return;
      }

      // console.log("✅ Payment method created:", paymentMethod.id); // SENSITIVE: Payment Method ID

      // Get authentication token
      const accessAuth0Token = await getSafeAccessAuth0Token();
      if (!accessAuth0Token) {
        console.error("❌ No auth token");
        setError("Authentication error. Please log in again.");
        setIsProcessing(false);
        return;
      }

      // Create subscription/payment with backend
      // console.log("💳 Processing payment with backend...", { paymentMethodId: paymentMethod.id, productId: selectedProductId }); // SENSITIVE: Payment Method ID
      console.log("💳 Processing payment with backend...", { productId: selectedProductId, promotionCodeId });
      const finalPrice = hasDiscount && discountedPrice !== null ? discountedPrice : productConfig?.price ?? 0;
      void trackBeginCheckout(analyticsAuthId, selectedProductId, planDurationFromProductId(selectedProductId), finalPrice);
      const result = await createStripeSubscription(accessAuth0Token, paymentMethod.id, selectedProductId, undefined, promotionCodeId);

      if (result.success) {
        // console.log("✅ Payment successful:", result); // SENSITIVE: May contain payment IDs
        console.log("✅ Payment successful");
        void trackPurchase(
          analyticsAuthId,
          result.stripeSubscriptionId || result.stripePaymentIntentId || "stripe",
          selectedProductId,
          finalPrice
        );

        // Call onSuccess with updated credits
        if (result.credits !== undefined) {
          onSuccess(result.credits);
        }

        // Stop processing and show success check immediately
        setIsProcessing(false);
        setShowSuccessCheck(true);
        console.log("✅ Check icon should now be visible");

        // Show payment success dialog with product information
        setPaymentDialogSuccess(true);
        // Create custom message with product details (use discounted price if available)
        // const finalPrice = hasDiscount && discountedPrice !== null ? discountedPrice : productConfig?.price;
        const productMessage = productConfig
          ? `You purchased ${productConfig.displayName} - $${finalPrice.toFixed(2)} ${productConfig.isSubscription ? "/month" : "one-time"} (${
              productConfig.creditsAmount
            } Credits)`
          : undefined;
        setPaymentDialogMessage(productMessage);
        setShowPaymentDialog(true);

        // After 2 seconds showing check icon, close the entire drawer
        setTimeout(() => {
          console.log("🚪 Closing drawer after check icon shown for 2 seconds");
          onDrawerClose();
        }, 2000);
      } else {
        console.error("❌ Payment failed:", result.message);
        void trackSubscriptionFailed(analyticsAuthId, selectedProductId, "failure", (result.message || "payment_failed").slice(0, 60));
        // NEVER show technical errors to users - always show friendly messages
        const friendlyMessage = getFriendlyErrorMessage(result.message);
        setError(friendlyMessage);
        setIsProcessing(false);

        // Don't show dialog for errors - only for success (as requested)
        // setPaymentDialogSuccess(false);
        // setPaymentDialogMessage(friendlyMessage);
        // setShowPaymentDialog(true);
      }
    } catch (error: any) {
      console.error("Payment error:", error);
      void trackSubscriptionFailed(analyticsAuthId, selectedProductId, "failure", "exception");
      // NEVER show technical errors to users
      const errorMessage = "An error occurred. Please try again.";
      setError(errorMessage);
      setIsProcessing(false);

      // Don't show dialog for errors - only for success (as requested)
      // setPaymentDialogSuccess(false);
      // setPaymentDialogMessage(errorMessage);
      // setShowPaymentDialog(true);
    }
  };

  // Helper function to convert technical errors to user-friendly messages
  const getFriendlyErrorMessage = (errorMessage: string | undefined): string => {
    if (!errorMessage) {
      return "Payment failed. Please try again.";
    }

    // Remove all technical details: stack traces, file paths, code snippets
    let cleanMessage = errorMessage
      .split("\n")[0] // Take only first line
      .split("at ")[0] // Remove stack traces
      .split("/Users/")[0] // Remove file paths
      .split("backend/")[0] // Remove backend paths
      .split("Invalid")[0] // Remove "Invalid..." errors
      .split("invocation")[0] // Remove Prisma invocation errors
      .split("prisma")[0] // Remove Prisma references
      .split("Expected")[0] // Remove type errors
      .trim();

    // If message is empty or too technical, use generic message
    if (!cleanMessage || cleanMessage.length < 5 || cleanMessage.includes("subscription.findFirst")) {
      return "Payment failed. Please try again or contact support.";
    }

    // Limit message length
    if (cleanMessage.length > 100) {
      cleanMessage = cleanMessage.substring(0, 100) + "...";
    }

    return cleanMessage;
  };

  return (
    <form onSubmit={handleSubmit} className={styles.formCheckoutForm}>
      {/* Product Summary */}
      {productConfig && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
            mb: 1.5,
            p: 1,
            pt: 1.5,
            pb: 1.5,
            bgcolor: PureLightThemeCaaS.colors.alpha.gray[5],
            borderRadius: 0.5,
            border: `0.5px solid ${PureLightThemeCaaS.colors.alpha.gray[30]}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 3,
              // justifyContent: "center",
              wrap: "wrap",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                flex: "0 0 auto",
                fontSize: "12px",
                // fontWeight: 600,
                color: PureLightThemeCaaS.colors.alpha.black[70],
                lineHeight: 1.2,
                textAlign: "left",
              }}
            >
              {productConfig.displayName}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25 }}>
              {hasDiscount && (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "11px",
                    color: PureLightThemeCaaS.colors.alpha.black[50],
                    textDecoration: "line-through",
                  }}
                >
                  ${productConfig.price.toFixed(2)} {productConfig.isSubscription ? "/month" : ""}
                </Typography>
              )}
              <Typography
                variant="h6"
                sx={{
                  flex: "0 0 auto",
                  fontSize: "12px",
                  color: hasDiscount ? PureLightThemeCaaS.colors.alpha.orange[100] : PureLightThemeCaaS.colors.alpha.black[70],
                  lineHeight: 1.2,
                  textAlign: "left",
                  fontWeight: hasDiscount ? 600 : 400,
                }}
              >
                ${hasDiscount ? discountedPrice.toFixed(2) : productConfig.price.toFixed(2)} {productConfig.isSubscription ? "/month" : "one-time"}
              </Typography>
              {hasDiscount && (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "11px",
                    textAlign: "center",
                    color: PureLightThemeCaaS.colors.alpha.orange[100],
                    fontWeight: 500,
                  }}
                >
                  {promoPercentOff ? `${promoPercentOff}% OFF` : promoAmountOff ? `$${promoAmountOff.toFixed(2)} OFF` : "Discount applied"}
                </Typography>
              )}
            </Box>
            <Typography
              variant="h6"
              sx={{
                flex: "0 0 auto",
                fontSize: "12px",
                // fontWeight: 600,
                color: PureLightThemeCaaS.colors.alpha.black[70],
                lineHeight: 1.2,
                textAlign: "left",
              }}
            >
              {productConfig.creditsAmount} Credits
            </Typography>
          </Box>
        </Box>
      )}

      {/* CardElement - Stripe Link disabled */}
      <Box sx={{ mb: 2 }}>
        <CardElement
          options={{
            // Disable Stripe Link (the "Usar link" button)
            disableLink: true,
          }}
        />
      </Box>

      {/* First Name */}
      <TextField
        fullWidth
        size="small"
        label="First Name"
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
        required
        sx={{
          mt: 0,
          mb: 1,
          "& .MuiOutlinedInput-root": {
            backgroundColor: PureLightThemeCaaS.colors.alpha.white[100],
            color: PureLightThemeCaaS.colors.alpha.black[100],
            "& fieldset": {
              borderColor: PureLightThemeCaaS.colors.info.lighter,
              borderWidth: "2px",
            },
            "&:hover fieldset": {
              borderColor: PureLightThemeCaaS.colors.info.lighter,
            },
            "&.Mui-focused fieldset": {
              borderColor: PureLightThemeCaaS.colors.info.lighter,
            },
          },
          "& .MuiInputLabel-root": {
            color: PureLightThemeCaaS.colors.alpha.black[70],
            "&.Mui-focused": {
              color: PureLightThemeCaaS.colors.alpha.black[100],
            },
            "&.MuiInputLabel-shrink": {
              color: PureLightThemeCaaS.colors.alpha.black[100],
            },
          },
        }}
      />

      {/* Last Name */}
      <TextField
        fullWidth
        size="small"
        label="Last Name"
        value={lastName}
        onChange={(e) => setLastName(e.target.value)}
        required
        sx={{
          mb: 1,
          "& .MuiOutlinedInput-root": {
            backgroundColor: PureLightThemeCaaS.colors.alpha.white[100],
            color: PureLightThemeCaaS.colors.alpha.black[100],
            "& fieldset": {
              borderColor: PureLightThemeCaaS.colors.info.lighter,
              borderWidth: "2px",
            },
            "&:hover fieldset": {
              borderColor: PureLightThemeCaaS.colors.info.lighter,
            },
            "&.Mui-focused fieldset": {
              borderColor: PureLightThemeCaaS.colors.info.lighter,
            },
          },
          "& .MuiInputLabel-root": {
            color: PureLightThemeCaaS.colors.alpha.black[70],
            "&.Mui-focused": {
              color: PureLightThemeCaaS.colors.alpha.black[100],
            },
            "&.MuiInputLabel-shrink": {
              color: PureLightThemeCaaS.colors.alpha.black[100],
            },
          },
        }}
      />

      {/* Address */}
      <TextField
        fullWidth
        size="small"
        label="Address"
        value={address}
        onChange={(e) => setAddress(e.target.value)}
        required
        sx={{
          mb: 1,
          "& .MuiOutlinedInput-root": {
            backgroundColor: PureLightThemeCaaS.colors.alpha.white[100],
            color: PureLightThemeCaaS.colors.alpha.black[100],
            "& fieldset": {
              borderColor: PureLightThemeCaaS.colors.info.lighter,
              borderWidth: "2px",
            },
            "&:hover fieldset": {
              borderColor: PureLightThemeCaaS.colors.info.lighter,
            },
            "&.Mui-focused fieldset": {
              borderColor: PureLightThemeCaaS.colors.info.lighter,
            },
          },
          "& .MuiInputLabel-root": {
            color: PureLightThemeCaaS.colors.alpha.black[70],
            "&.Mui-focused": {
              color: PureLightThemeCaaS.colors.alpha.black[100],
            },
            "&.MuiInputLabel-shrink": {
              color: PureLightThemeCaaS.colors.alpha.black[100],
            },
          },
        }}
      />

      {/* Country Selection */}
      <Autocomplete
        size="small"
        value={selectedCountry}
        onChange={handleCountryChange}
        options={countries}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.code === value.code}
        openOnFocus={false}
        disablePortal={false}
        disableClearable={false}
        sx={{
          mt: 0,
          mb: 1,
          width: "100%",
          "& .MuiAutocomplete-inputRoot": {
            backgroundColor: PureLightThemeCaaS.colors.alpha.white[100],
            color: PureLightThemeCaaS.colors.alpha.black[100],
            "& fieldset": {
              borderColor: PureLightThemeCaaS.colors.info.lighter,
              borderWidth: "2px",
            },
            "&:hover fieldset": {
              borderColor: PureLightThemeCaaS.colors.info.lighter,
            },
            "&.Mui-focused fieldset": {
              borderColor: PureLightThemeCaaS.colors.info.lighter,
            },
          },
          "& .MuiAutocomplete-input": {
            color: PureLightThemeCaaS.colors.alpha.black[100],
          },
          "& .MuiAutocomplete-popper": {
            zIndex: 9999,
          },
        }}
        slotProps={{
          popper: {
            style: {
              zIndex: 9999,
            },
            placement: "top-start",
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [0, 4],
                },
              },
              {
                name: "flip",
                enabled: false, // Disable automatic flipping - always open downward
              },
              {
                name: "preventOverflow",
                enabled: true,
                options: {
                  boundary: "viewport",
                },
              },
              {
                name: "sameWidth",
                enabled: true,
                fn: ({ state }) => {
                  state.styles.popper.width = `${state.rects.reference.width}px`;
                },
                phase: "beforeWrite",
                requires: ["computeStyles"],
              },
            ],
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            InputLabelProps={{ shrink: false }}
            inputProps={{
              ...params.inputProps,
              style: {
                color: PureLightThemeCaaS.colors.alpha.black[100],
              },
            }}
            placeholder="Select Country"
            onFocus={(event) => (event.target.placeholder = "")}
            onBlur={(event) => {
              if (event.target.value === "") {
                event.target.placeholder = "Select Country";
              }
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: PureLightThemeCaaS.colors.alpha.white[100],
                color: PureLightThemeCaaS.colors.alpha.black[100],
                "& fieldset": {
                  borderColor: PureLightThemeCaaS.colors.info.lighter,
                  borderWidth: "2px",
                },
                "&:hover fieldset": {
                  borderColor: PureLightThemeCaaS.colors.info.lighter,
                },
                "&.Mui-focused fieldset": {
                  borderColor: PureLightThemeCaaS.colors.info.lighter,
                },
              },
              "& .MuiOutlinedInput-input::placeholder": {
                color: PureLightThemeCaaS.colors.alpha.black[70],
                opacity: 1,
                fontSize: "14px",
                fontFamily: "inherit",
              },
              "& .MuiSvgIcon-root": {
                color: "black",
              },
            }}
          />
        )}
        PaperComponent={(props) => (
          <Paper
            {...props}
            sx={{
              backgroundColor: "white",
              color: "black",
              maxHeight: "200px",
              overflow: "auto",
              boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
              marginTop: "4px",
            }}
          />
        )}
        ListboxProps={{
          style: {
            maxHeight: "200px",
            padding: "4px 0",
          },
        }}
        renderOption={(props, option) => (
          <Box
            component="li"
            {...props}
            key={option.code}
            sx={{
              padding: "8px 16px",
              cursor: "pointer",
              backgroundColor: "transparent !important", // Reset any default background
              "&:hover, &.Mui-focused, &[aria-selected='true']": {
                backgroundColor: "#f5f5f5 !important",
              },
            }}
          >
            {option.label}
          </Box>
        )}
      />
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        {error && (
          <Typography color={`${PureLightThemeCaaS.colors.alpha.orange[100]}`} sx={{ position: "absolute", mt: 3, pl: 2, pr: 2 }}>
            {error}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
        <Button
          type="submit"
          disabled={!isValid || isProcessing || showSuccessCheck}
          variant="contained"
          sx={{
            bgcolor:
              isProcessing || showSuccessCheck
                ? `${PureLightThemeCaaS.colors.alpha.gray[10]} !important`
                : isValid
                ? `${PureLightThemeCaaS.colors.alpha.orange[100]} !important`
                : `${PureLightThemeCaaS.colors.alpha.gray[10]} !important`,
            "&.Mui-disabled": {
              bgcolor: `${PureLightThemeCaaS.colors.alpha.gray[10]} !important`,
            },
            borderRadius: 2,
            width: "30%",
            height: "30px",
            textAlign: "center",
            p: 0.5,
            fontSize: "14px",
            transition: "background-color 0.3s ease",
            cursor: showSuccessCheck ? "default" : "pointer",
          }}
        >
          {isProcessing ? (
            <CircularProgress size={20} sx={{ color: `${PureLightThemeCaaS.colors.alpha.orange[100]}` }} />
          ) : showSuccessCheck ? (
            <CheckCircleIcon sx={{ fontSize: 24, color: "#36960c" }} />
          ) : (
            "PAY"
          )}
        </Button>
      </Box>

      {/* Payment Dialog */}
      <PaymentDialog open={showPaymentDialog} onClose={() => setShowPaymentDialog(false)} success={paymentDialogSuccess} message={paymentDialogMessage} autoCloseDelay={4000} />
    </form>
  );
};

export default CheckoutForm;
